import { useMemo } from "react";
import type { SupportedSession } from "../../../types";
import { useTellescopeQuery } from "../../use-tellescope-query";

interface UseAccessibleEnduserIdsArgs {
  /** The deduped enduser IDs to test for accessibility. */
  enduserIds: readonly string[];
  /**
   * Whether the accessibility fetch is relevant (i.e. some requested action
   * resolves to `Assigned`). When `false`, no fetch is issued and the result is
   * empty — the common evaluator only consults the accessible set under
   * `Assigned`, so the other access types are unaffected.
   */
  enabled: boolean;
  session?: SupportedSession;
}

/**
 * Resolves which of `enduserIds` the current user can access by issuing the
 * access-restricted `endusers.getByIds` read — the same read the backend uses
 * to build `assignedEnduserIds`. An enduser is accessible iff the read returns
 * it, which already accounts for `All` / `assignedTo` / `accessTags` / creator
 * enduser access. The result feeds the evaluator's `Assigned` decision.
 */
export const useAccessibleEnduserIds = ({
  enduserIds,
  enabled,
  session,
}: UseAccessibleEnduserIdsArgs): { accessibleEnduserIds: Set<string>; isLoading: boolean } => {
  const fetchEnabled = enabled && enduserIds.length > 0;

  const query = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: { ids: [...enduserIds] },
    enabled: fetchEnabled,
    session,
  });

  const accessibleEnduserIds = useMemo(
    () => new Set((query.data?.matches ?? []).map((enduser) => enduser.id)),
    [query.data]
  );

  return { accessibleEnduserIds, isLoading: fetchEnabled && query.isLoading };
};
